import { GlobalToken } from 'antd/es/theme/interface';
import type { DataNode } from 'antd/es/tree';
import { BPRCProcess, OrgLevel } from 'store/ducks/Reporting/types';
import {
  renderOrgNode,
  renderProcessNode,
  renderRiskNode,
  renderProcessControlNode
} from './renderOrgNode';

const ORG_LEVELS: OrgLevel[] = [
  'group',
  'division',
  'region',
  'legal_entity',
  'business_unit'
];

interface OrgNode {
  id: number;
  name: string;
  level: OrgLevel;
  children: Map<string, OrgNode>;
  processes: BPRCProcess[];
}

const getOrgValue = (
  process: BPRCProcess,
  level: OrgLevel
): { id: number; name: string } | null => {
  const map: Record<OrgLevel, { id: number; name: string } | null> = {
    group: process.group
      ? { id: process.group.id, name: process.group.name }
      : null,
    division: process.division
      ? { id: process.division.id, name: process.division.name }
      : null,
    region: process.region
      ? { id: process.region.id, name: process.region.name }
      : null,
    legal_entity: process.legal_entity
      ? { id: process.legal_entity.id, name: process.legal_entity.name }
      : null,
    business_unit: process.business_unit
      ? { id: process.business_unit.id, name: process.business_unit.name }
      : null
  };
  return map[level];
};

export const buildOrgTree = (processes: BPRCProcess[]): OrgNode[] => {
  const roots = new Map<string, OrgNode>();

  for (const process of processes) {
    const directHierarchy = (process.direct_hierarchy ?? 'group') as OrgLevel;
    const levelIndex = ORG_LEVELS.indexOf(directHierarchy);
    const levelsToUse = ORG_LEVELS.slice(0, levelIndex + 1);

    let currentMap = roots;
    let currentNode: OrgNode | null = null;

    for (const level of levelsToUse) {
      const org = getOrgValue(process, level);
      if (!org) break;

      const key = `${level}-${org.id}`;
      if (!currentMap.has(key)) {
        const node: OrgNode = {
          id: org.id,
          name: org.name,
          level,
          children: new Map(),
          processes: []
        };
        currentMap.set(key, node);
      }
      currentNode = currentMap.get(key)!;
      currentMap = currentNode.children;
    }

    if (currentNode) currentNode.processes.push(process);
  }

  return Array.from(roots.values());
};

export const buildTreeData = (
  orgNodes: OrgNode[],
  token: GlobalToken
): DataNode[] => {
  const mapOrgNode = (node: OrgNode, keyPrefix: string): DataNode => {
    const key = `${keyPrefix}-${node.level}-${node.id}`;

    const childOrgNodes: DataNode[] = Array.from(node.children.values()).map(
      (child) => mapOrgNode(child, key)
    );

    const processNodes: DataNode[] = node.processes.map((process) => {
      const processKey = `${key}-process-${process.id}`;

      const risks = process.risks ?? [];
      const riskNodes: DataNode[] = risks.length
        ? risks.map((risk) => {
            const riskKey = `${processKey}-risk-${risk.id}`;
            const controls = risk.controls ?? [];
            const controlNodes: DataNode[] = controls.length
              ? controls.map((control) => ({
                  key: `${riskKey}-control-${control.id}`,
                  title: renderProcessControlNode(control.control_name, token)
                }))
              : [
                  {
                    key: `${riskKey}-control-empty`,
                    title: renderProcessControlNode(null, token)
                  }
                ];

            return {
              key: riskKey,
              title: renderRiskNode(risk.title, token),
              children: controlNodes
            };
          })
        : [
            {
              key: `${processKey}-risk-empty`,
              title: renderRiskNode(null, token),
              children: [
                {
                  key: `${processKey}-risk-empty-control-empty`,
                  title: renderProcessControlNode(null, token)
                }
              ]
            }
          ];

      return {
        key: processKey,
        title: renderProcessNode(process, token),
        children: riskNodes
      };
    });

    const allChildren = [...childOrgNodes, ...processNodes];
    return {
      key,
      title: renderOrgNode(node.name, node.level, token),
      children: allChildren.length ? allChildren : undefined
    };
  };

  return orgNodes.map((node) => mapOrgNode(node, 'root'));
};
